import { Injectable } from '@angular/core';
import { AuthService } from 'app/shared/services/core/auth.service';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';

/**
 * Interface para definição de permissão de rota
 */
export interface RoutePermission {
  route: string;
  allowedProfiles: PerfilEnum[];
}

/**
 * Serviço responsável por gerenciar as permissões de usuário
 * baseadas no perfil (perfilId) retornado no JWT.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionCheckService {

  /**
   * Mapeamento de rotas para perfis permitidos
   * Baseado na documentação do backend (AUTHORIZATION.md)
   */
  private readonly routePermissions: RoutePermission[] = [
    // Rotas exclusivas ADM_SISTEMA (cadastros administrativos)
    { route: '/imobiliaria', allowedProfiles: [PerfilEnum.ADM_SISTEMA] },
    { route: '/trilha', allowedProfiles: [PerfilEnum.ADM_SISTEMA] },

    // Rotas ADM_IMOBILIARIA e ADM_SISTEMA
    { route: '/usuario', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA] },
    { route: '/minha-assinatura', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA] },
    { route: '/minha-imobiliaria', allowedProfiles: [PerfilEnum.ADM_IMOBILIARIA] },
    { route: '/dashboard', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.PARCEIRO] },

    // Rotas ADM_SISTEMA e PARCEIRO
    { route: '/comissao', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.PARCEIRO] },

    // Rotas acessíveis a todos os perfis autenticados
    { route: '/inicio', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/academia-edduca', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/modulo', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/aula', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/aulas-ao-vivo', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/edducai', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/ranking', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/badges', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/certificados', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/feedbacks', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/newsletter', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/comunicados', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
    { route: '/meu-perfil', allowedProfiles: [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.USUARIO_IMOBILIARIA, PerfilEnum.PARCEIRO] },
  ];

  constructor(private authService: AuthService) {}

  /**
   * Obtém o perfilId do usuário logado a partir do token JWT
   */
  getPerfilId(): PerfilEnum | null {
    const tokenInfo = this.authService.getTokenInfo();
    if (tokenInfo) {
      // Verifica diferentes possíveis nomes da claim (case-insensitive)
      const perfilId = tokenInfo.perfilId || tokenInfo.PerfilId || tokenInfo.perfil_id || tokenInfo.perfilid;
      if (perfilId) {
        return Number(perfilId) as PerfilEnum;
      }
    }
    return null;
  }

  /**
   * Verifica se o usuário é ADM_SISTEMA (tem acesso total)
   */
  isAdmSistema(): boolean {
    return this.getPerfilId() === PerfilEnum.ADM_SISTEMA;
  }

  /**
   * Verifica se o usuário é ADM_IMOBILIARIA ou superior
   */
  isAdmImobiliariaOrHigher(): boolean {
    const perfilId = this.getPerfilId();
    return perfilId === PerfilEnum.ADM_SISTEMA || perfilId === PerfilEnum.ADM_IMOBILIARIA;
  }

  /**
   * Verifica se o usuário tem um dos perfis especificados.
   * NOTA: Esta verificação é ESTRITA - o perfil deve estar explicitamente na lista.
   * Use hasProfileWithAdminBypass() se quiser que ADM_SISTEMA tenha acesso automático.
   * @param allowedProfiles Lista de perfis permitidos
   */
  hasProfile(...allowedProfiles: PerfilEnum[]): boolean {
    const perfilId = this.getPerfilId();

    // Se não tem perfil, não tem permissão
    if (perfilId === null) return false;

    // Verificação estrita: o perfil deve estar explicitamente na lista
    // Isso permite restringir features específicas para perfis específicos
    // Ex: "Minha Imobiliária" só deve aparecer para ADM_IMOBILIARIA, não para ADM_SISTEMA
    return allowedProfiles.includes(perfilId);
  }

  /**
   * Verifica se o usuário tem um dos perfis especificados OU é ADM_SISTEMA.
   * Use este método quando ADM_SISTEMA deve ter acesso automático (bypass de permissão).
   * @param allowedProfiles Lista de perfis permitidos
   */
  hasProfileWithAdminBypass(...allowedProfiles: PerfilEnum[]): boolean {
    const perfilId = this.getPerfilId();

    // Se não tem perfil, não tem permissão
    if (perfilId === null) return false;

    // ADM_SISTEMA sempre tem acesso total (conforme AUTHORIZATION.md do backend)
    if (perfilId === PerfilEnum.ADM_SISTEMA) return true;

    // Verifica se o perfil do usuário está na lista de permitidos
    return allowedProfiles.includes(perfilId);
  }

  /**
   * Verifica se o usuário tem permissão para acessar uma rota específica.
   * Rotas sem regra explícita são consideradas permitidas para usuários autenticados.
   * @param url URL da rota a ser verificada
   */
  hasRoutePermission(url: string): boolean {
    const perfilId = this.getPerfilId();

    // Se não tem perfil definido mas está autenticado, permite acesso a rotas públicas
    // Isso garante que usuários autenticados não sejam bloqueados por problemas de claim
    if (perfilId === null) {
      // Verifica se está autenticado
      if (this.authService.isTokenValid()) {
        // Se está autenticado mas sem perfilId, permite rotas públicas
        const publicRoutes = ['/inicio', '/academia-edduca', '/modulo', '/aula', '/aulas-ao-vivo', 
          '/edducai', '/ranking', '/badges', '/certificados', '/feedbacks', '/newsletter', '/comunicados', '/meu-perfil'];
        const urlParts = url.split('/').filter(part => part !== '');
        const routeBase = urlParts.length > 0 ? '/' + urlParts[0] : '/';
        return publicRoutes.some(r => url.startsWith(r) || routeBase === r);
      }
      return false;
    }

    // ADM_SISTEMA sempre tem acesso total (conforme AUTHORIZATION.md do backend)
    if (perfilId === PerfilEnum.ADM_SISTEMA) return true;

    // Validação de URL - evita erros com URLs malformadas
    if (!url || url === '/' || url.trim() === '') {
      return true; // Rota raiz é permitida
    }

    // Encontra a permissão para a rota
    const urlParts = url.split('/').filter(part => part !== '');
    const routeBase = urlParts.length > 0 ? '/' + urlParts[0] : '/';

    const permission = this.routePermissions.find(p =>
      url.startsWith(p.route) || routeBase === p.route
    );

    // Se não encontrou regra específica, permite acesso (rota pública para autenticados)
    // Isso é intencional: rotas como /inicio, /ranking, etc. são permitidas a todos os autenticados
    if (!permission) return true;

    // Verifica se o perfil do usuário está na lista de permitidos
    return permission.allowedProfiles.includes(perfilId);
  }

  /**
   * Retorna a lista de rotas permitidas para o perfil do usuário
   */
  getAllowedRoutes(): string[] {
    const perfilId = this.getPerfilId();

    if (perfilId === null) return [];

    // ADM_SISTEMA tem acesso a todas as rotas
    if (perfilId === PerfilEnum.ADM_SISTEMA) {
      return this.routePermissions.map(p => p.route);
    }

    // Filtra apenas as rotas permitidas para o perfil
    return this.routePermissions
      .filter(p => p.allowedProfiles.includes(perfilId))
      .map(p => p.route);
  }

  /**
   * Verifica se o usuário pode ver o menu "Minha Assinatura"
   * Disponível para: ADM_SISTEMA, ADM_IMOBILIARIA
   */
  canViewMinhaAssinatura(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA);
  }

  /**
   * Verifica se o usuário pode ver o menu "Usuários"
   * Disponível para: ADM_SISTEMA, ADM_IMOBILIARIA
   */
  canViewUsuarios(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA);
  }

  /**
   * Verifica se o usuário pode ver o menu "Imobiliárias"
   * Disponível para: ADM_SISTEMA
   */
  canViewImobiliarias(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA);
  }

  /**
   * Verifica se o usuário pode ver os cadastros (Trilhas, Módulos, Aulas)
   * Disponível para: ADM_SISTEMA
   */
  canViewCadastros(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA);
  }

  /**
   * Verifica se o usuário pode criar comunicados
   * Disponível para: ADM_SISTEMA, ADM_IMOBILIARIA
   */
  canCreateComunicados(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA);
  }

  /**
   * Verifica se o usuário pode criar newsletters
   * Disponível para: ADM_SISTEMA
   */
  canCreateNewsletter(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA);
  }

  /**
   * Verifica se o usuário pode ver comissões
   * Disponível para: ADM_SISTEMA, ADM_IMOBILIARIA
   */
  canViewComissoes(): boolean {
    return this.hasProfile(PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA);
  }
}
